/**
 * 统一格式化工具
 * 整合format.ts和date.ts中的重复函数，提供统一的格式化接口
 * 
 * 功能模块：
 * - 日期时间格式化
 * - 数字格式化
 * - 价格格式化
 * - 文件大小格式化
 * - 持续时间格式化
 * 
 * @author AI Assistant
 * @since 2025-01-12
 * @version 2.0.0
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/zh-cn';

// 扩展 dayjs 插件
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

// 设置默认语言为中文
dayjs.locale('zh-cn');

// ============= 类型定义 =============

export type DateInput = dayjs.ConfigType;
export type NumberInput = number | null | undefined;
export type StringInput = string | null | undefined;

// ============= 日期时间格式化 =============

/**
 * 统一的日期格式化器
 */
export const dateFormatter = {
  /**
   * 格式化日期
   * @param date 日期
   * @param format 格式字符串，默认 'YYYY-MM-DD HH:mm:ss'
   * @returns 格式化后的日期字符串
   */
  format(date?: DateInput, format = 'YYYY-MM-DD HH:mm:ss'): string {
    if (!date) return '-';
    return dayjs(date).format(format);
  },

  /**
   * 格式化为短日期
   * @param date 日期
   * @returns YYYY-MM-DD 格式
   */
  formatDate(date?: DateInput): string {
    if (!date) return '-';
    return dayjs(date).format('YYYY-MM-DD');
  },

  /**
   * 格式化为时间
   * @param date 日期
   * @returns HH:mm:ss 格式
   */
  formatTime(date?: DateInput): string {
    if (!date) return '-';
    return dayjs(date).format('HH:mm:ss');
  },

  /**
   * 获取相对时间
   * @param date 日期
   * @returns 相对时间字符串，如 "2小时前"
   */
  getRelativeTime(date: DateInput): string {
    return dayjs(date).fromNow();
  },

  /**
   * 获取友好的日期显示
   * @param date 日期
   * @returns 友好的日期字符串
   */
  getFriendlyDate(date: DateInput): string {
    const target = dayjs(date);
    const now = dayjs();
    
    if (target.isSame(now, 'day')) {
      return `今天 ${target.format('HH:mm')}`;
    }
    
    if (target.isSame(now.subtract(1, 'day'), 'day')) {
      return `昨天 ${target.format('HH:mm')}`;
    }
    
    if (target.isSame(now, 'year')) {
      return target.format('MM-DD HH:mm');
    }
    
    return target.format('YYYY-MM-DD HH:mm');
  },

  /**
   * 检查日期是否为今天
   * @param date 日期
   * @returns 是否为今天
   */
  isToday(date: DateInput): boolean {
    return dayjs(date).isSame(dayjs(), 'day');
  },

  /**
   * 检查日期是否为昨天
   * @param date 日期
   * @returns 是否为昨天
   */
  isYesterday(date: DateInput): boolean {
    return dayjs(date).isSame(dayjs().subtract(1, 'day'), 'day');
  },

  /**
   * 获取时间戳
   * @param date 日期，默认为当前时间
   * @returns Unix 时间戳（秒）
   */
  getTimestamp(date?: DateInput): number {
    return dayjs(date).unix();
  },

  /**
   * 从时间戳创建日期
   * @param timestamp Unix 时间戳（秒）
   * @returns dayjs 对象
   */
  fromTimestamp(timestamp: number): dayjs.Dayjs {
    return dayjs.unix(timestamp);
  },

  /**
   * 获取毫秒时间戳
   * @param date 日期，默认为当前时间
   * @returns 毫秒时间戳
   */
  getMillisTimestamp(date?: DateInput): number {
    return dayjs(date).valueOf();
  },

  /**
   * 从毫秒时间戳创建日期
   * @param timestamp 毫秒时间戳
   * @returns dayjs 对象
   */
  fromMillisTimestamp(timestamp: number): dayjs.Dayjs {
    return dayjs(timestamp);
  }
};

// ============= 数字格式化 =============

/**
 * 统一的数字格式化器
 */
export const numberFormatter = {
  /**
   * 格式化数字，添加千位分隔符
   * @param num 要格式化的数字
   * @param defaultValue 默认值
   * @returns 格式化后的数字字符串
   */
  format(num: NumberInput, defaultValue = '-'): string {
    if (num === null || num === undefined) return defaultValue;
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  /**
   * 格式化为百分比
   * @param num 数字（0-1之间）
   * @param decimals 小数位数
   * @returns 百分比字符串
   */
  formatPercentage(num: NumberInput, decimals = 1): string {
    if (num === null || num === undefined) return '-';
    return `${(num * 100).toFixed(decimals)}%`;
  },

  /**
   * 格式化为紧凑数字显示（K、M、B等）
   * @param num 数字
   * @param decimals 小数位数
   * @returns 紧凑格式的数字字符串
   */
  formatCompact(num: NumberInput, decimals = 1): string {
    if (num === null || num === undefined) return '-';
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(decimals) + 'B';
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(decimals) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(decimals) + 'K';
    }
    return num.toString();
  },

  /**
   * 格式化为固定小数位
   * @param num 数字
   * @param decimals 小数位数
   * @returns 格式化后的数字字符串
   */
  formatDecimal(num: NumberInput, decimals = 2): string {
    if (num === null || num === undefined) return '-';
    return num.toFixed(decimals);
  }
};

// ============= 价格格式化 =============

/**
 * 统一的价格格式化器
 */
export const priceFormatter = {
  /**
   * 分转元（保留2位小数）
   * @param cents 分
   * @returns 元字符串
   */
  formatPrice(cents: NumberInput): string {
    if (cents === null || cents === undefined) return '0.00';
    return (cents / 100).toFixed(2);
  },

  /**
   * 元转分
   * @param yuan 元
   * @returns 分
   */
  priceToFen(yuan: number | string): number {
    return Math.round(Number(yuan) * 100);
  },

  /**
   * 格式化价格显示
   * @param cents 分
   * @param currency 货币符号
   * @returns 格式化的价格字符串
   */
  formatPriceDisplay(cents: NumberInput, currency = '¥'): string {
    if (cents === null || cents === undefined) return `${currency}0.00`;
    return `${currency}${this.formatPrice(cents)}`;
  }
};

// ============= 文件大小格式化 =============

/**
 * 统一的文件大小格式化器
 */
export const fileSizeFormatter = {
  /**
   * 格式化文件大小
   * @param bytes 字节数
   * @param decimals 小数位数
   * @returns 格式化后的文件大小字符串
   */
  formatFileSize(bytes: NumberInput, decimals = 2): string {
    if (bytes === null || bytes === undefined || bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
};

// ============= 持续时间格式化 =============

/**
 * 统一的持续时间格式化器
 */
export const durationFormatter = {
  /**
   * 格式化持续时间（时间码格式）
   * @param seconds 秒数
   * @returns 格式化的时间字符串，如 "01:23"
   */
  formatDuration(seconds: NumberInput): string {
    if (seconds === null || seconds === undefined) return '00:00';

    const duration = dayjs.duration(seconds, 'seconds');
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const secs = duration.seconds();

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  },

  /**
   * 格式化持续时间（中文格式）
   * @param seconds 秒数
   * @returns 格式化的时间字符串，如 "1分23秒"、"1小时5分"
   */
  formatDurationChinese(seconds: NumberInput): string {
    if (seconds === null || seconds === undefined) return '0秒';

    const sec = Math.floor(seconds);

    if (sec < 60) {
      return `${sec}秒`;
    }

    if (sec < 3600) {
      const minutes = Math.floor(sec / 60);
      const remainingSeconds = sec % 60;
      return remainingSeconds > 0 ? `${minutes}分${remainingSeconds}秒` : `${minutes}分钟`;
    }

    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    return minutes > 0 ? `${hours}小时${minutes}分钟` : `${hours}小时`;
  },

  /**
   * 获取时间差
   * @param start 开始时间
   * @param end 结束时间
   * @returns 时间差对象
   */
  getDuration(start: DateInput, end?: DateInput) {
    return dayjs.duration(dayjs(end).diff(dayjs(start)));
  }
};

// ============= 任务格式化 =============

import {
  TASK_STATUS_TEXT_MAP,
  TASK_STATUS_TYPE_MAP,
  TASK_TYPE_TEXT_MAP
} from './app-runtime';

/**
 * 统一的任务格式化器
 */
export const taskFormatter = {
  /**
   * 获取任务状态文本
   * @param status 任务状态
   * @returns 状态文本
   */
  getStatusText(status: string): string {
    return TASK_STATUS_TEXT_MAP[status as keyof typeof TASK_STATUS_TEXT_MAP] || status;
  },

  /**
   * 获取任务状态类型（用于UI显示）
   * @param status 任务状态
   * @returns 状态类型
   */
  getStatusType(status: string): 'success' | 'warning' | 'danger' | 'info' {
    return (TASK_STATUS_TYPE_MAP[status as keyof typeof TASK_STATUS_TYPE_MAP] || 'info') as 'success' | 'warning' | 'danger' | 'info';
  },

  /**
   * 获取任务类型文本
   * @param type 任务类型
   * @returns 类型文本
   */
  getTypeText(type: string): string {
    return TASK_TYPE_TEXT_MAP[type as keyof typeof TASK_TYPE_TEXT_MAP] || type;
  }
};

// ============= 用户格式化 =============

import {
  USER_TIER_TEXT_MAP,
  USER_TIER_COLOR_MAP
} from './app-runtime';

/**
 * 统一的用户格式化器
 */
export const userFormatter = {
  /**
   * 获取用户层级名称
   * @param tier 用户层级
   * @returns 层级名称
   */
  getTierName(tier: string): string {
    return USER_TIER_TEXT_MAP[tier as keyof typeof USER_TIER_TEXT_MAP] || tier;
  },

  /**
   * 获取用户层级颜色
   * @param tier 用户层级
   * @returns 颜色值
   */
  getTierColor(tier: string): string {
    return USER_TIER_COLOR_MAP[tier as keyof typeof USER_TIER_COLOR_MAP] || '#909399';
  }
};

// ============= 统一导出 =============

/**
 * 统一格式化器
 * 提供所有格式化功能的统一入口
 */
export const formatters = {
  date: dateFormatter,
  number: numberFormatter,
  price: priceFormatter,
  fileSize: fileSizeFormatter,
  duration: durationFormatter,
  task: taskFormatter,
  user: userFormatter
};

// 导出兼容性函数（保持向后兼容）
export const formatDate = dateFormatter.format;
export const formatNumber = numberFormatter.format;
export const formatPrice = priceFormatter.formatPrice;
export const priceToFen = priceFormatter.priceToFen;
export const formatFileSize = fileSizeFormatter.formatFileSize;
export const formatDuration = durationFormatter.formatDuration;
export const formatDurationChinese = durationFormatter.formatDurationChinese;
export const getTaskStatusText = taskFormatter.getStatusText;
export const getTaskStatusType = taskFormatter.getStatusType;
export const getTaskTypeText = taskFormatter.getTypeText;
export const getUserTierName = userFormatter.getTierName;
export const getUserTierColor = userFormatter.getTierColor;

// 导出 dayjs 实例供直接使用
export { dayjs };

// ============= SSML格式化 =============

/**
 * 格式化SSML字符串，使其具有良好的缩进和换行
 * @param ssml 原始SSML字符串
 * @returns 格式化后的SSML字符串
 */
export function formatSSML(ssml: string): string {
  if (!ssml || typeof ssml !== 'string') {
    return '';
  }

  try {
    // 移除多余的空白字符
    let formatted = ssml.replace(/>\s+</g, '><').trim();

    // 定义需要换行的标签
    const blockTags = ['speak', 'voice', 'mstts:express-as', 'prosody', 'break'];
    const indentLevel = '  '; // 两个空格作为缩进

    let result = '';
    let currentIndent = 0;
    let i = 0;

    while (i < formatted.length) {
      if (formatted[i] === '<') {
        // 找到标签的结束位置
        const tagEnd = formatted.indexOf('>', i);
        if (tagEnd === -1) break;

        const tag = formatted.substring(i, tagEnd + 1);
        const tagName = extractTagName(tag);

        // 判断是否是结束标签
        const isClosingTag = tag.startsWith('</');
        const isSelfClosing = tag.endsWith('/>');

        if (isClosingTag) {
          currentIndent--;
        }

        // 添加缩进和标签
        if (blockTags.includes(tagName)) {
          if (result && !result.endsWith('\n')) {
            result += '\n';
          }
          result += indentLevel.repeat(Math.max(0, currentIndent)) + tag;

          if (!isSelfClosing && !isClosingTag) {
            result += '\n';
            currentIndent++;
          } else if (isClosingTag) {
            result += '\n';
          }
        } else {
          result += tag;
        }

        i = tagEnd + 1;
      } else {
        // 处理文本内容
        let textEnd = formatted.indexOf('<', i);
        if (textEnd === -1) textEnd = formatted.length;

        const text = formatted.substring(i, textEnd).trim();
        if (text) {
          // 如果前面是换行，添加适当的缩进
          if (result.endsWith('\n')) {
            result += indentLevel.repeat(currentIndent) + text;
          } else {
            result += text;
          }
        }

        i = textEnd;
      }
    }

    return result.trim();
  } catch (error) {
    console.warn('SSML格式化失败:', error);
    return ssml;
  }
}

/**
 * 从标签字符串中提取标签名
 * @param tag 标签字符串，如 "<voice name='test'>" 或 "</voice>"
 * @returns 标签名，如 "voice"
 */
function extractTagName(tag: string): string {
  const match = tag.match(/<\/?([a-zA-Z:]+)/);
  return match ? match[1] : '';
}

/**
 * 压缩SSML，移除不必要的空白字符
 * @param ssml SSML字符串
 * @returns 压缩后的SSML字符串
 */
export function compressSSML(ssml: string): string {
  if (!ssml || typeof ssml !== 'string') {
    return '';
  }

  return ssml
    .replace(/>\s+</g, '><')  // 移除标签间的空白
    .replace(/\s+/g, ' ')     // 将多个空白字符合并为单个空格
    .trim();                  // 移除首尾空白
}

/**
 * 验证SSML格式是否正确
 * @param ssml SSML字符串
 * @returns 是否为有效的SSML
 */
export function isValidSSML(ssml: string): boolean {
  if (!ssml || typeof ssml !== 'string') {
    return false;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(ssml, 'text/xml');
    const parseError = doc.querySelector('parsererror');
    return !parseError;
  } catch {
    return false;
  }
}

/**
 * 美化SSML显示，添加语法高亮的类名（用于CSS样式）
 * @param ssml SSML字符串
 * @returns 带有样式类的HTML字符串
 */
export function beautifySSMLForDisplay(ssml: string): string {
  if (!ssml) return '';

  return formatSSML(ssml)
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/(&lt;\/?)([a-zA-Z:]+)/g, '$1<span class="ssml-tag">$2</span>')
    .replace(/(\w+)=("[^"]*")/g, '<span class="ssml-attr">$1</span>=<span class="ssml-value">$2</span>');
}

// ============= 导出 =============



export default formatters;
